/**
 * React hook for consistent pricing calculations
 *
 * This hook provides a React interface to the centralized pricing service,
 * handling state management and memoization for optimal performance.
 */

import { useMemo } from "react";
import { PricingService } from "@shared/pricing-service";
import { quoteToPricingInput } from "@shared/pricing-utils";
import type {
  PricingInput,
  PricingResult,
  DiscountInfo,
} from "@shared/pricing-types";
import type {
  Service,
  QuoteService,
  QuoteProduct,
  Product,
  CustomPart,
  QuoteCustomPart,
  Quote,
} from "@onsemble/shared";
import type { QuoteTemplate } from "@shared/schema";

interface UsePricingOptions {
  // Enable/disable automatic recalculation
  autoCalculate?: boolean;
}

interface UsePricingReturn {
  // Current pricing result
  pricing: PricingResult;

  // Convenience getters
  subtotalBeforeDiscount: number;
  subtotalAfterDiscount: number;
  discountAmount: number;
  finalTotal: number;

  // Validation
  isValid: boolean;
  errors: string[];

  // Manual calculation trigger
  calculate: (input: PricingInput) => PricingResult;

  // Product-specific calculations
  calculateProductPricing: (
    products: QuoteProduct[],
    productsData: Product[],
    markupPercent: number,
    partsAffectPrice: boolean,
  ) => ReturnType<typeof PricingService.calculateProductPricing>;
}

/**
 * Hook for getting pricing calculations with automatic updates
 */
function usePricing(
  input: PricingInput,
  options: UsePricingOptions = {},
): UsePricingReturn {
  const { autoCalculate = true } = options;

  // Calculate pricing directly - computation is cheap enough to run on every render
  const pricing = autoCalculate
    ? PricingService.calculatePricing(input)
    : ({
        laborCost: 0,
        servicesCost: 0,
        customPartsCost: 0,
        partsCost: 0,
        partsCostPremarkup: 0,
        partsCostPostmarkup: 0,
        servicesLaborTotal: 0,
        subtotalBeforeDiscount: 0,
        discountAmount: 0,
        subtotalAfterDiscount: 0,
        taxAmount: 0,
        finalTotal: 0,
        breakdown: {
          labor: { hours: 0, rate: 0, total: 0 },
          parts: {
            subtotal: 0,
            markupPercent: 0,
            markupAmount: 0,
            total: 0,
            affectsPrice: true,
          },
          services: [],
          customParts: [],
          discount: {
            type: "percentage" as const,
            value: 0,
            description: "",
            amount: 0,
          },
          tax: { amount: 0 },
        },
      } as PricingResult);

  // Memoize validation
  const validation = useMemo(() => {
    return PricingService.validatePricingInput(input);
  }, [input]);

  // Manual calculation function
  const calculate = useMemo(() => {
    return (newInput: PricingInput) =>
      PricingService.calculatePricing(newInput);
  }, []);

  // Product pricing calculation
  const calculateProductPricing = useMemo(() => {
    return (
      products: QuoteProduct[],
      productsData: Product[],
      markupPercent: number,
      partsAffectPrice: boolean,
    ) =>
      PricingService.calculateProductPricing(
        products,
        productsData,
        markupPercent,
        partsAffectPrice,
      );
  }, []);

  return {
    pricing,
    subtotalBeforeDiscount: pricing.subtotalBeforeDiscount,
    subtotalAfterDiscount: pricing.subtotalAfterDiscount,
    discountAmount: pricing.discountAmount,
    finalTotal: pricing.finalTotal,
    isValid: validation.isValid,
    errors: validation.errors,
    calculate,
    calculateProductPricing,
  };
}

/**
 * Input type for useFormPricing hook
 * Represents form data structure for pricing calculations
 */
interface FormPricingInput {
  laborHours?: number;
  laborRate?: number;
  products?: QuoteProduct[];
  productsData?: Product[];
  markupPercent?: number;
  partsAffectPrice?: boolean;
  services?: Service[];
  quoteServices?: QuoteService[];
  customParts?: CustomPart[];
  quoteCustomParts?: QuoteCustomPart[];
  discount?: DiscountInfo;
  // Legacy support for old discount format
  discountPercentage?: number;
  discountDescription?: string;
}

/**
 * Hook for real-time pricing updates in forms
 */
export function useFormPricing(formData: FormPricingInput): UsePricingReturn {
  // Convert legacy discount format to new format if needed
  let discount: DiscountInfo | undefined = formData.discount;
  if (!discount && formData.discountPercentage) {
    discount = {
      type: "percentage",
      value: formData.discountPercentage,
      description: formData.discountDescription || "",
    };
  }

  const input: PricingInput = {
    laborHours: formData.laborHours || 0,
    laborRate: formData.laborRate || 0,
    products: formData.products || [],
    productsData: formData.productsData || [],
    markupPercent: formData.markupPercent || 0,
    partsAffectPrice: formData.partsAffectPrice ?? true,
    services: formData.services || [],
    quoteServices: formData.quoteServices || [],
    customParts: formData.customParts || [],
    quoteCustomParts: formData.quoteCustomParts || [],
    discount,
  };

  return usePricing(input);
}

/**
 * Convenience hook for calculating pricing directly from Quote/QuoteTemplate data
 *
 * This hook combines quoteToPricingInput transformation with useFormPricing
 * to provide a clean one-line API for getting pricing from quote data.
 *
 * Automatically handles memoization and recalculation when dependencies change.
 *
 * @param quote - Quote, QuoteTemplate, or form data to calculate pricing for
 * @param products - Full product catalog for price lookup
 * @param services - Service catalog (optional)
 * @param customParts - Custom parts catalog (optional)
 * @returns UsePricingReturn with calculated pricing and helper methods
 *
 * @example
 * // In QuoteViewer
 * const pricing = useQuotePricing(data, products, services, customParts);
 * console.log(pricing.finalTotal); // Access calculated totals
 *
 * @example
 * // For draft quotes - recalculates from current prices
 * const pricing = useQuotePricing(draftQuote, products, services, customParts);
 *
 * @example
 * // For locked quotes - you probably want stored amounts instead
 * // This hook is primarily for draft quotes and previews
 */
export function useQuotePricing(
  quote: Quote | QuoteTemplate | any,
  products: Product[],
  services?: Service[],
  customParts?: CustomPart[],
): UsePricingReturn {
  const input = useMemo(
    () => quoteToPricingInput(quote, products, services, customParts),
    [quote, products, services, customParts],
  );

  return useFormPricing(input);
}
